const IMPRESSION_MIN_VISIBLE_MS = 500

export interface SpanTracker {
  setVisible(serveId: string, visible: boolean): void
  setActive(active: boolean): void
  ensureImpression(serveId: string): void
  now(): number
  dispose(): void
}

interface SpanTrackerOptions {
  onImpression: (serveId: string, occurredAt: number) => void
  now?: () => number
}

interface Span {
  start: number
  timer: ReturnType<typeof setTimeout> | null
}

export const createSpanTracker = ({ onImpression, now = () => Date.now() }: SpanTrackerOptions): SpanTracker => {
  const visible = new Set<string>()
  const spans = new Map<string, Span>()
  const impressed = new Set<string>()
  let active = true
  let disposed = false

  const emit = (serveId: string, occurredAt: number) => {
    impressed.add(serveId)
    onImpression(serveId, occurredAt)
  }

  const startSpan = (serveId: string) => {
    if (disposed) return
    const start = now()
    const span: Span = { start, timer: null }
    span.timer = setTimeout(() => {
      span.timer = null
      emit(serveId, start)
    }, IMPRESSION_MIN_VISIBLE_MS)
    spans.set(serveId, span)
  }

  const endSpan = (serveId: string) => {
    const span = spans.get(serveId)
    if (span?.timer != null) clearTimeout(span.timer)
    spans.delete(serveId)
  }

  return {
    setVisible(serveId, isVisible) {
      if (disposed || visible.has(serveId) === isVisible) return
      if (isVisible) {
        visible.add(serveId)
        if (active) startSpan(serveId)
      } else {
        visible.delete(serveId)
        endSpan(serveId)
      }
    },
    setActive(isActive) {
      if (active === isActive) return
      active = isActive
      for (const serveId of visible) {
        if (isActive) startSpan(serveId)
        else endSpan(serveId)
      }
    },
    ensureImpression(serveId) {
      if (impressed.has(serveId)) return
      const span = spans.get(serveId)
      if (span?.timer != null) {
        clearTimeout(span.timer)
        span.timer = null
        emit(serveId, span.start)
        return
      }
      emit(serveId, now())
    },
    now,
    dispose() {
      disposed = true
      for (const serveId of spans.keys()) endSpan(serveId)
      visible.clear()
    },
  }
}

export const recordClick = (sink: { click: (serveId: string, occurredAt: number) => void }, tracker: SpanTracker, serveId: string): void => {
  tracker.ensureImpression(serveId)
  sink.click(serveId, tracker.now())
}
